import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!r.ok) throw new Error("Not authenticated");
      setUser(await r.json());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user) {
      setFavorites(new Set());
      return;
    }
    fetch(`${API}/me/favorites`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setFavorites(new Set(list.map((e) => e.slug))))
      .catch(() => {});
  }, [user]);

  const toggleFavorite = async (slug) => {
    const isSaved = favorites.has(slug);
    const method = isSaved ? "DELETE" : "POST";
    const url = isSaved ? `${API}/me/favorites/${slug}` : `${API}/me/favorites`;
    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: isSaved ? undefined : JSON.stringify({ slug }),
    });
    setFavorites((prev) => {
      const next = new Set(prev);
      if (isSaved) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setFavorites(new Set());
  };

  const login = async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(typeof d.detail === "string" ? d.detail : "Errore di accesso");
    }
    const u = await r.json();
    setUser(u);
    return u;
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, login, logout, favorites, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
